<script setup lang="ts">
import { computed } from 'vue';

/**
 * Track quality tier pill for list cards — mirrors {@link SeverityBadge} sizing
 * so rating + severity sit on one baseline. Tier colour follows the first
 * letter of the stored rating (A → purple, B → slate blue, C → red); the
 * explicit `NR` (Not Rated) placeholder is red so unrated tracks stand out;
 * anything else uses muted styling.
 */
const props = withDefaults(
  defineProps<{
    rating?: string | null;
    /**
     * Alias for `rating`, so the shared filter-builder dropdown wrappers
     * ({@link FilterEnumSelect}, {@link FilterMultiCombobox}) can pass options
     * through with their generic `{ value, label }` shape.
     */
    value?: string | null;
    label?: string | null;
  /** Unused. Declared so FilterMultiCombobox's `:option` stays out of the DOM. */
  option?: unknown;
    /** Matches {@link SeverityBadge} density for table (`sm`) vs cards (`md`). */
    size?: 'sm' | 'md';
  }>(),
  { size: 'md' },
);

const display = computed(() => (props.rating ?? props.value ?? '').trim().toUpperCase());

const tierClass = computed(() => {
  if (display.value === 'NR') {
    // Not Rated: red, so the gap is as loud as a C-tier problem.
    return 'bg-red-500/20 text-red-700 dark:bg-red-500/25 dark:text-red-300';
  }
  const first = display.value.charAt(0);
  if (first === 'A') {
    return 'bg-violet-500/20 text-violet-900 dark:text-violet-200';
  }
  if (first === 'B') {
    // User-provided reference blue (~#8294C4) for B-tier backgrounds.
    return 'bg-[#8294C4]/35 text-[#2f3d5c] dark:bg-[#8294C4]/28 dark:text-[#dce4fb]';
  }
  if (first === 'C') {
    // Dark `--destructive` is a muted red; lift the pill + text so it reads like A/B in dark mode.
    return 'bg-destructive/15 text-destructive dark:bg-destructive/25 dark:text-red-300';
  }
  return 'bg-muted text-muted-foreground';
});

const sizeClass = computed(() =>
  props.size === 'sm'
    ? 'px-1.5 py-0.5 text-[10px]'
    : 'px-1.5 py-0.5 text-[11px] font-medium',
);
</script>

<template>
  <span
    v-if="display"
    class="rounded uppercase tracking-wide"
    :class="[tierClass, sizeClass]"
    data-testid="track-rating-badge"
  >
    {{ display }}
  </span>
</template>
